import { FrontDoorInput } from '@cpd-app/generated';

import {
  FrontDoorAnswers,
  FrontDoorIntent,
  FrontDoorVoice
} from '../../components/front-door/front-door.model';

/**
 * FD-1. The rule for what the front door shows, kept out of the container so
 * it can be tested without a store.
 *
 * - `hidden`: neither the door nor the strip. Anyone with no saved answer who
 *   has done the Skills Survey, signed-in members with no saved answer, and
 *   anyone we cannot decide for yet.
 * - `door`: the full-screen question. A first visit, and only that.
 * - `strip`: the one-line plan. Answered before, on this device or any other;
 *   for a signed-in member, from their account. It stays after the Skills
 *   Survey, naming the next step.
 * - `pending`: still deciding, for someone the browser has never seen answer
 *   or sign in. The door's navy screen holds the page so home never flashes
 *   before the door; it becomes the door, or lifts to show home.
 */
export type FrontDoorMode = 'hidden' | 'door' | 'strip' | 'pending';

export interface FrontDoorVisibility {
  /** The profile has arrived. Until then nothing is decided. */
  profileLoaded: boolean;
  signedIn: boolean;
  /** Skills Survey done: they are past the point the door is for. */
  skillsSurveyStarted: boolean;
  /** The server lookup has settled, answered, empty, failed or skipped. */
  serverChecked: boolean;
  serverAnswers: FrontDoorAnswers | null;
  localAnswers: FrontDoorAnswers | null;
  /**
   * Nothing on this device says they have been here: no answer and no
   * sign-in. Known at once, before anything loads, which is what lets the
   * page hold the door's screen instead of flashing home.
   */
  mayBeFirstVisit?: boolean;
  /** A signed-in counselor or administrator (QA.20): never the learner door. */
  staff?: boolean;
}

/** The seven the subgraph accepts. Mirrors the CHECK on api.userfrontdoor. */
const INTENTS: readonly FrontDoorIntent[] = [
  'benefit',
  'job',
  'explore',
  'degree',
  'credit',
  'return',
  'other'
];

const hasIntent = (answers: FrontDoorAnswers | null): boolean =>
  !!answers?.intent;

/**
 * The answer to show, the server's first: it is the account's record and the
 * one that follows the visitor across devices. This device's copy is the
 * fallback for when the server has none or could not be reached.
 */
export function answeredDoor(
  serverAnswers: FrontDoorAnswers | null,
  localAnswers: FrontDoorAnswers | null
): FrontDoorAnswers | null {
  if (hasIntent(serverAnswers)) {
    return serverAnswers;
  }
  return hasIntent(localAnswers) ? localAnswers : null;
}

export function frontDoorMode(state: FrontDoorVisibility): FrontDoorMode {
  // QA.20: the door and the learner plan strip are for learners and guests.
  // A staff session came to Home for the shared-report band, which the door
  // would cover.
  if (state.staff) {
    return 'hidden';
  }
  const waiting: FrontDoorMode = state.mayBeFirstVisit ? 'pending' : 'hidden';

  // Past the point the door is for, so never the door. But the one-line plan
  // stays (walkthrough 09-29 #7): after the Skills Survey is exactly when the
  // member needs telling what comes next, and the strip is where that is said.
  if (state.skillsSurveyStarted) {
    const saved = state.signedIn
      ? answeredDoor(state.serverAnswers, null)
      : answeredDoor(state.serverAnswers, state.localAnswers);
    return state.profileLoaded && saved ? 'strip' : 'hidden';
  }
  // A member never sees the door, but does see the plan they answered for,
  // read from their account (never this device's copy, which may be a
  // different person's on a shared machine).
  if (state.signedIn) {
    return state.profileLoaded && answeredDoor(state.serverAnswers, null)
      ? 'strip'
      : 'hidden';
  }
  if (!state.profileLoaded) {
    return waiting;
  }
  if (answeredDoor(state.serverAnswers, state.localAnswers)) {
    return 'strip';
  }
  // Nothing on this device: wait for the server rather than open the door
  // and then swap it for the strip when a saved answer arrives.
  return state.serverChecked ? 'door' : waiting;
}

/** A saved row as the door's model, or null when it is not one we know. */
export function fromServerAnswers(
  row: { intent: string; intentText?: string | null } | null | undefined
): FrontDoorAnswers | null {
  if (!row || !INTENTS.includes(row.intent as FrontDoorIntent)) {
    return null;
  }
  return {
    intent: row.intent as FrontDoorIntent,
    intentText: row.intentText ?? ''
  };
}

/** What saveFrontDoor is sent, or null when there is nothing to save. */
export function toSaveFrontDoorInput(
  answers: FrontDoorAnswers,
  voice: FrontDoorVoice
): FrontDoorInput | null {
  if (!answers?.intent) {
    return null;
  }
  const text = (answers.intentText ?? '').trim();
  return { intent: answers.intent, intentText: text || null, voice };
}
